import { useState, useEffect } from "react";
import { CategoryFilter } from "../CategoryFilter";
import { SearchBar } from "../SearchBar";
import { ToolsGrid } from "../ToolsGrid";
import { useTools } from "@/hooks/useTools";
import { Loader2 } from "lucide-react";
import { Tool } from "@/types";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";

export const ToolsSection = () => {
  const [selectedCategory, setSelectedCategory] = useState("الكل");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredTools, setFilteredTools] = useState<Tool[]>([]);
  const [visibleTools, setVisibleTools] = useState<Tool[]>([]);
  const [visibleCount, setVisibleCount] = useState(6);
  const { toast } = useToast();

  const { data, isLoading, isError, error } = useTools({
    currentPage: 1,
    selectedCategory: "الكل",
    searchQuery: "",
    itemsPerPage: 100,
  });

  useEffect(() => {
    if (data?.tools) {
      let filtered = [...data.tools];

      if (selectedCategory !== "الكل") {
        filtered = filtered.filter(tool => 
          tool.categories.includes(selectedCategory)
        );
      }

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(tool =>
          tool.name.toLowerCase().includes(query) ||
          tool.description.toLowerCase().includes(query)
        );

        // Show toast when search yields no results
        if (filtered.length === 0) {
          toast({
            title: "لا توجد نتائج",
            description: "جرب البحث بكلمات مختلفة",
            variant: "destructive",
          });
        }
      }

      // Sort featured tools to the top
      filtered.sort((a, b) => {
        if (a.is_featured && !b.is_featured) return -1;
        if (!a.is_featured && b.is_featured) return 1;
        return 0;
      });

      setFilteredTools(filtered);
      setVisibleCount(6);
    }
  }, [data?.tools, selectedCategory, searchQuery, toast]);

  useEffect(() => {
    setVisibleTools(filteredTools.slice(0, visibleCount));
  }, [filteredTools, visibleCount]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 6);
  };

  if (isError) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="text-center text-red-500 p-6 rounded-lg bg-red-50 dark:bg-red-900/10 shadow-sm"
      >
        <p className="font-semibold text-lg mb-2">حدث خطأ أثناء تحميل الأدوات</p>
        <p className="text-sm">{error instanceof Error ? error.message : 'يرجى المحاولة مرة أخرى'}</p>
      </motion.div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.5 }}
        className="space-y-8"
      >
        {isLoading ? (
          <div className="flex flex-col items-center justify-center min-h-[300px] space-y-4">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
            <p className="text-muted-foreground">جاري تحميل الأدوات...</p>
          </div>
        ) : (
          <>
            <div className="space-y-6">
              <SearchBar onSearch={handleSearch} />
              <div className="flex flex-col space-y-4">
                <CategoryFilter
                  selectedCategory={selectedCategory}
                  onCategoryChange={handleCategoryChange}
                />
              </div>
            </div>

            <div className="space-y-8">
              <ToolsGrid 
                tools={visibleTools}
                isLoading={isLoading}
              />

              {visibleCount < filteredTools.length && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex justify-center mt-8"
                >
                  <Button
                    onClick={handleLoadMore}
                    className="px-8 py-2"
                    size="lg"
                    variant="secondary"
                  >
                    عرض المزيد من الأدوات
                  </Button>
                </motion.div>
              )}
            </div>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};